import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';

interface BaseProps {
  children: ReactNode;
  className?: string;
  id?: string;
}

/**
 * A layout container for multi-pane worksurfaces.
 * Replaces: <div className="flex flex-1 overflow-hidden">
 */
export function WorksurfaceLayout({ children, className }: BaseProps) {
  return (
    <div className={cn("flex flex-1 w-full min-h-0 overflow-hidden", className)}>
      {children}
    </div>
  );
}

/**
 * A structural sidebar for worksurfaces.
 * Replaces: <div className="w-[380px] border-r ... flex flex-col">
 */
export function SidebarPane({ 
  children, 
  className, 
  position = 'left', 
  width = 380 
}: BaseProps & { position?: 'left' | 'right', width?: number | string }) {
  return (
    <div 
      style={{ width: typeof width === 'number' ? `${width}px` : width }}
      className={cn(
        "flex flex-col shrink-0 h-full bg-muted/5",
        position === 'left' ? "border-r border-border/40" : "border-l border-border/40",
        className
      )}
    >
      {children}
    </div>
  );
}

/**
 * The primary content area in a WorksurfaceLayout.
 * Replaces: <div className="flex-1 relative flex flex-col bg-muted/10">
 */
export function MainPane({ children, className }: BaseProps) {
  return (
    <div className={cn("flex-1 relative flex flex-col min-w-0 bg-muted/10", className)}>
      {children}
    </div>
  );
}

/**
 * A standard scrollable container for panes.
 * Replaces: <div className="flex-1 overflow-y-auto">
 */
export function Scrollable({ children, className }: BaseProps) {
  return (
    <div className={cn("flex-1 overflow-y-auto min-h-0 scrollbar-thin", className)}>
      {children}
    </div>
  );
}

/**
 * An absolute-positioned overlay for things like hover cards or map tools.
 */
export function Overlay({ 
  children, 
  className, 
  position = 'inset' 
}: BaseProps & { position?: 'inset' | 'top-right' | 'bottom-left' | 'bottom-right' }) {
  const positionClasses = {
    inset: 'absolute inset-0',
    'top-right': 'absolute top-4 right-4 z-10',
    'bottom-left': 'absolute bottom-8 left-8 z-50',
    'bottom-right': 'absolute bottom-8 right-8 z-50',
  }[position];

  return (
    <div className={cn(positionClasses, className)}>
      {children}
    </div>
  );
}
